import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { 
  Sun, 
  LayoutDashboard, 
  Users, 
  Building2, 
  Package, 
  FileText, 
  Receipt, 
  ClipboardList,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/leads', label: 'Leads', icon: Users },
  { path: '/parties', label: 'Parties', icon: Building2 },
  { path: '/items', label: 'Items', icon: Package },
  { path: '/quotations', label: 'Quotations', icon: FileText },
  { path: '/proforma-invoices', label: 'Proforma Invoices', icon: Receipt },
  { path: '/soa', label: 'SOA', icon: ClipboardList },
  { path: '/settings', label: 'Settings', icon: Settings },
];

export const Layout = ({ children }) => {
  const { userData, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Failed to log out');
    }
  };

  const NavLinkItem = ({ item, onClick }) => (
    <NavLink
      to={item.path}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors touch-target ${
          isActive
            ? 'bg-amber-100 text-amber-900 font-medium'
            : 'text-slate-600 hover:bg-slate-100'
        }`
      }
      data-testid={`nav-${item.path.replace('/', '')}`}
    >
      <item.icon className="w-5 h-5" strokeWidth={1.5} />
      <span>{item.label}</span>
      <ChevronRight className="w-4 h-4 ml-auto opacity-50" />
    </NavLink>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between glass">
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="touch-target" data-testid="mobile-menu-btn">
              <Menu className="w-6 h-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[280px] p-0">
            <div className="flex flex-col h-full">
              {/* Logo */}
              <div className="p-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full solar-gradient flex items-center justify-center">
                    <Sun className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h1 className="font-semibold text-slate-900">SUNSTORE</h1>
                    <p className="text-xs text-slate-500">Solar CRM</p>
                  </div>
                </div>
              </div>

              {/* User Info */}
              <div className="p-4 bg-slate-50">
                <p className="font-medium text-slate-900 truncate">{userData?.displayName || 'User'}</p>
                <p className="text-xs text-slate-500 truncate">{userData?.email}</p>
                <span className="inline-block mt-1 px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-800 capitalize">
                  {userData?.role || 'user'}
                </span>
              </div>

              {/* Navigation */}
              <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                {navItems.map(item => (
                  <NavLinkItem 
                    key={item.path} 
                    item={item} 
                    onClick={() => setMobileMenuOpen(false)} 
                  />
                ))}
              </nav>

              {/* Logout */}
              <div className="p-4 border-t border-slate-100">
                <Button
                  variant="outline"
                  className="w-full justify-start gap-3 text-red-600 border-red-200 hover:bg-red-50"
                  onClick={handleSignOut}
                  data-testid="logout-btn"
                >
                  <LogOut className="w-5 h-5" />
                  Sign Out
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>

        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full solar-gradient flex items-center justify-center">
            <Sun className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold text-slate-900">SUNSTORE</span>
        </div>

        <div className="w-10" /> {/* Spacer for centering */}
      </header>

      {/* Desktop Sidebar */}
      <aside className="hidden md:flex fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-slate-200 flex-col z-40">
        {/* Logo */}
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full solar-gradient flex items-center justify-center shadow-lg">
              <Sun className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-lg text-slate-900">SUNSTORE</h1>
              <p className="text-xs text-slate-500">KOLHAPUR CRM</p>
            </div>
          </div>
        </div>

        {/* User Info */}
        <div className="p-4 mx-4 mt-4 rounded-lg bg-slate-50">
          <p className="font-medium text-slate-900 truncate">{userData?.displayName || 'User'}</p>
          <p className="text-xs text-slate-500 truncate">{userData?.email}</p>
          <span className="inline-block mt-2 px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-800 capitalize">
            {userData?.role || 'user'}
          </span>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-amber-100 text-amber-900 font-medium'
                    : 'text-slate-600 hover:bg-slate-100'
                }`
              }
              data-testid={`desktop-nav-${item.path.replace('/', '')}`}
            >
              <item.icon className="w-5 h-5" strokeWidth={1.5} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Logout */}
        <div className="p-4 border-t border-slate-100">
          <Button
            variant="outline"
            className="w-full justify-start gap-3 text-red-600 border-red-200 hover:bg-red-50"
            onClick={handleSignOut}
            data-testid="desktop-logout-btn"
          >
            <LogOut className="w-5 h-5" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="md:ml-64 min-h-screen pt-16 md:pt-0">
        {children}
      </main>
    </div>
  );
};

export default Layout;
